import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { HrefLink } from '@bloomays-lib/ui.shared';
import { StatusChip } from '@bloomays-lib/ui.shared/datagrid';
import {
  convertDateToStringFormat,
  getPrivateAdminDashboardCraDashboardPath,
  getPrivateAdminDashboardMissionPath,
} from '@bloomays-lib/utils.shared';
import { DashboardPurchaseOrder, PurchaseOrderDisplayStatus } from '../../helpers/purchaseOrderDashboard';
import { PURCHASE_ORDER_DISPLAY_STATUS_TONE } from '../../helpers/purchaseOrderSemanticStatus';
import type { ExistingPurchaseOrderRow } from './ReceivedPurchaseOrderTab';

export type PurchaseOrderDetailPanelProps = {
  open: boolean;
  row: ExistingPurchaseOrderRow | null;
  onClose: () => void;
  onEdit: (purchaseOrder: DashboardPurchaseOrder) => void;
  onUpload: (purchaseOrder: DashboardPurchaseOrder) => void;
  /** Open the secured file preview popin (mirrors the CRA drawer's invoice preview). */
  onPreviewFile: (purchaseOrder: DashboardPurchaseOrder) => void;
};

const formatAmount = (value: number | undefined | null): string => {
  if (value === undefined || value === null) {
    return '—';
  }
  return `${value.toLocaleString('fr-FR')} €`;
};

const formatDate = (value: string | Date | undefined | null): string => {
  if (!value) {
    return '—';
  }
  return convertDateToStringFormat(value as string, 'dd/MM/yyyy');
};

const formatPeriod = (start: string | Date | undefined | null, end: string | Date | undefined | null): string =>
  `${formatDate(start)} → ${formatDate(end)}`;

export const PurchaseOrderDetailPanel = ({
  open,
  row,
  onClose,
  onEdit,
  onUpload,
  onPreviewFile,
}: PurchaseOrderDetailPanelProps) => {
  const { t } = useTranslation(['purchaseOrderDashboard']);

  const statusLabel: Record<PurchaseOrderDisplayStatus, string> = {
    received: t('statusReceived'),
    partial: t('statusPartial'),
    waiting: t('statusWaiting'),
    expired: t('statusExpired'),
    soldout: t('statusSoldout'),
  };

  if (!row) {
    return null;
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Panel data-testid="purchase-order-detail-panel">
        <Head>
          <div>
            <Client>{row.referenceSubtitle}</Client>
            <MissionLabel>{row.missionLabel}</MissionLabel>
          </div>
          <IconButton aria-label="close-purchase-order-detail" size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Head>

        <ChipRow>
          <StatusChip tone={PURCHASE_ORDER_DISPLAY_STATUS_TONE[row.displayStatus]}>
            {statusLabel[row.displayStatus]}
          </StatusChip>
          <StatusChip tone="neutral">{row.orderNumber}</StatusChip>
        </ChipRow>

        <Section>
          <SectionTitle>{t('coverageDetailSectionMission')}</SectionTitle>
          <Line>
            <Key>{t('colMission')}</Key>
            <Val>{row.missionLabel}</Val>
          </Line>
          <Line>
            <Key>{t('detailClient')}</Key>
            <Val>{row.referenceSubtitle}</Val>
          </Line>
          <Line>
            <Key>{t('detailBloomer')}</Key>
            <Val>{row.bloomerName ?? '—'}</Val>
          </Line>
          <Line>
            <Key>{t('detailAm')}</Key>
            <Val>{row.amShort}</Val>
          </Line>
        </Section>

        <Section>
          <SectionTitle>{t('purchaseOrderDetailSectionDetails')}</SectionTitle>
          <Line>
            <Key>{t('colEngagedAmount')}</Key>
            <Val>{formatAmount(row.amountHT)}</Val>
          </Line>
          <Line>
            <Key>{t('detailRemainingAmount')}</Key>
            <Val>{formatAmount(row.remaining)}</Val>
          </Line>
          <Line>
            <Key>{t('colValidity')}</Key>
            <Val>{formatPeriod(row.startDate, row.endDate)}</Val>
          </Line>
        </Section>

        <Actions>
          <ActionLink type="button" aria-label="purchase-order-detail-edit" onClick={() => onEdit(row.raw)}>
            <EditOutlinedIcon fontSize="small" />
            {t('editAction')}
          </ActionLink>
          {row.fileUrl ? (
            <ActionLink
              type="button"
              aria-label="purchase-order-detail-view-file"
              onClick={() => onPreviewFile(row.raw)}
            >
              <VisibilityOutlinedIcon fontSize="small" />
              {t('viewFile')}
            </ActionLink>
          ) : (
            <ActionLink type="button" aria-label="purchase-order-detail-upload" onClick={() => onUpload(row.raw)}>
              <UploadFileOutlinedIcon fontSize="small" />
              {t('uploadFile')}
            </ActionLink>
          )}
        </Actions>

        <LinksRow>
          {row.missionRecordId ? (
            <HrefLink
              href={getPrivateAdminDashboardMissionPath(row.missionRecordId)}
              target="_self"
              color="tertiary"
              text={t('purchaseOrderDetailViewMission')}
              icon={<OpenInNewOutlinedIcon fontSize="small" />}
            />
          ) : null}
          {/* Vue CRA filtrée sur ce BDC (recherche pré-remplie), scopée à la mission quand elle est connue
              pour ne pas dépendre du mois affiché par défaut sur la vue globale (TEC-3291). */}
          <HrefLink
            href={getPrivateAdminDashboardCraDashboardPath(row.orderNumber, row.missionRecordId)}
            target="_self"
            color="tertiary"
            text={t('purchaseOrderDetailViewActivities')}
            icon={<EventNoteOutlinedIcon fontSize="small" />}
          />
        </LinksRow>
      </Panel>
    </Drawer>
  );
};

export default PurchaseOrderDetailPanel;

const Panel = styled('div')(({ theme }) => ({
  width: 'min(420px, 100vw)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: '20px 24px 28px',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.neutral.white,
}));

const Head = styled('div')(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
}));

const Client = styled('div')(({ theme }) => ({
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: 18,
  fontWeight: 700,
  color: theme.palette.neutral[90],
}));

const MissionLabel = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[60],
  marginTop: 2,
}));

const ChipRow = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
}));

const Section = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  paddingTop: 16,
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
}));

const SectionTitle = styled('div')(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.neutral[50],
}));

const Line = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
}));

const Key = styled('span')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.neutral[60],
}));

const Val = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.neutral[90],
  textAlign: 'right',
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  paddingTop: 16,
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
}));

const ActionLink = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  background: 'none',
  padding: 0,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: '"Poppins Medium", sans-serif',
  color: theme.palette.blue[60],
  cursor: 'pointer',
  width: 'fit-content',
  '&:hover': { textDecoration: 'underline' },
}));

const LinksRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  paddingTop: 16,
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
}));
